import { canManageCustomerService } from '@cpm/shared';
import { CustomerServiceCreate } from '../../components/customer-services.js';
import { CustomerDatabaseCreate } from '../../components/customer-databases.js';
import { CustomerMailPanel } from '../../components/mail-panels.js';
import { SubscriptionFilesPanel } from '../../components/files-panels.js';
import { MAIL_MANAGE_FEATURE, FILES_MANAGE_FEATURE } from '@cpm/shared';
import { DnsSummary } from '../../components/dns-summary.js';
import { useMe } from '../../api/auth.js';
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  PortalBackupsDto,
  PortalFileAccessDto,
  PortalSubscriptionDetailDto,
  SftpCredentialDto,
} from '@cpm/shared';
import { formatLimitAmount } from '@cpm/shared';
import { ArrowLeft, Boxes, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../api/client.js';
import { Modal } from '../../components/Modal.js';
import { SubscriptionProtectionPanel } from '../../components/subscription-protection-panel.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  MeterBar,
  PageHeader,
  Spinner,
  statusTone,
} from '../../components/ui.js';

function BackupsCard({ subscriptionId }: { subscriptionId: string }) {
  const backups = useQuery({
    queryKey: ['portal', 'subscriptions', subscriptionId, 'backups'],
    queryFn: () => api.get<PortalBackupsDto>(`/api/portal/subscriptions/${subscriptionId}/backups`),
  });

  if (backups.isPending) return <Spinner />;
  if (backups.error) return <ErrorNote error={backups.error} />;
  const data = backups.data;
  if (!data.moduleEnabled) return null;

  return (
    <Card>
      <CardHeader title="Backups" description="Recent backup runs for your databases" />
      {!data.featureEnabled ? (
        <p className="px-5 pb-4 text-sm text-slate-500 dark:text-slate-400">
          Backups are not included in your plan.
        </p>
      ) : data.databases.length === 0 ? (
        <p className="px-5 pb-4 text-sm text-slate-500 dark:text-slate-400">
          No databases in this subscription.
        </p>
      ) : (
        <div className="space-y-4 px-5 pb-4">
          {data.databases.map((db) => (
            <div key={db.resourceId}>
              <div className="mb-1.5 flex items-center justify-between text-sm">
                <span className="font-medium">{db.name}</span>
                {db.schedules[0] && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    schedule: <code>{db.schedules[0].frequency}</code>
                    {db.schedules[0].enabled ? '' : ' (paused)'}
                  </span>
                )}
              </div>
              {db.error ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">{db.error}</p>
              ) : !db.engineSupported ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  This database engine has no scheduled backups.
                </p>
              ) : db.executions.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">No backup runs yet.</p>
              ) : (
                <ul className="space-y-1">
                  {db.executions.slice(0, 5).map((run) => (
                    <li key={run.uuid} className="flex items-center gap-2 text-sm">
                      <Badge tone={statusTone(run.status)}>{run.status}</Badge>
                      <span className="text-slate-600 dark:text-slate-300">
                        {run.createdAt ? new Date(run.createdAt).toLocaleString() : '—'}
                      </span>
                      {run.sizeBytes != null && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {(run.sizeBytes / 1024).toFixed(1)} KiB
                        </span>
                      )}
                      {run.message && (
                        <span className="truncate text-xs text-red-600 dark:text-red-400">
                          {run.message}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function FilesCard({ subscriptionId }: { subscriptionId: string }) {
  const [credential, setCredential] = useState<SftpCredentialDto | null>(null);
  const files = useQuery({
    queryKey: ['portal', 'subscriptions', subscriptionId, 'files'],
    queryFn: () =>
      api.get<PortalFileAccessDto>(`/api/portal/subscriptions/${subscriptionId}/files`),
  });
  const reset = useMutation({
    mutationFn: (userId: string) =>
      api.post<SftpCredentialDto>(
        `/api/portal/subscriptions/${subscriptionId}/files/${userId}/reset-password`,
        {},
      ),
    onSuccess: (cred) => setCredential(cred),
  });

  if (files.isPending) return <Spinner />;
  if (files.error) return <ErrorNote error={files.error} />;
  const data = files.data;
  if (!data.moduleEnabled) return null;

  return (
    <>
      <Card>
        <CardHeader title="File access" description="SFTP & browser file manager" />
        {!data.featureEnabled || data.accounts.length === 0 ? (
          <p className="px-5 pb-4 text-sm text-slate-500 dark:text-slate-400">
            {data.featureEnabled
              ? 'No file accounts have been assigned yet.'
              : 'File access is not included in your plan.'}
          </p>
        ) : (
          <div className="space-y-3 px-5 pb-5">
            {data.accounts.map((access) => (
              <div
                key={access.id}
                className="rounded border border-slate-200 p-3 dark:border-slate-800"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{access.label}</span>
                  <Badge tone={access.status === 'active' ? 'green' : 'yellow'}>
                    {access.status}
                  </Badge>
                  <span className="ml-auto font-mono text-xs">{access.username}</span>
                </div>
                <p className="mt-1 font-mono text-xs text-slate-500">
                  {access.sftpHost ?? '—'}
                  {access.sftpPort != null ? `:${access.sftpPort}` : ''}
                </p>
                <ul className="mt-2 divide-y divide-slate-100 text-sm dark:divide-slate-800">
                  {access.folders.map((folder) => (
                    <li key={folder.virtualPath} className="flex items-center gap-2 py-1.5">
                      <span className="font-mono text-xs">{folder.virtualPath}</span>
                      <Badge tone={folder.mode === 'ro' ? 'gray' : 'green'}>{folder.mode}</Badge>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex flex-wrap gap-2">
                  {access.webClientUrl && access.status === 'active' && (
                    <a href={access.webClientUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="secondary">
                        Open file manager <ExternalLink className="ml-1 size-4" />
                      </Button>
                    </a>
                  )}
                  {access.status === 'active' && (
                    <Button
                      variant="secondary"
                      loading={reset.isPending}
                      onClick={() => reset.mutate(access.id)}
                    >
                      Rotate SFTP password
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {reset.error != null && <ErrorNote error={reset.error} />}
          </div>
        )}
      </Card>
      {credential && (
        <Modal title="New SFTP password" open onClose={() => setCredential(null)}>
          <p className="mb-3 text-sm text-amber-600 dark:text-amber-400">
            Copy your password now — it is shown once and cannot be retrieved later.
          </p>
          <p className="font-mono text-xs break-all">{credential.password}</p>
          <div className="mt-4 flex justify-end">
            <Button onClick={() => setCredential(null)}>Done</Button>
          </div>
        </Modal>
      )}
    </>
  );
}

export function PortalSubscriptionDetailPage() {
  const me = useMe();
  const { id } = useParams<{ id: string }>();
  const detail = useQuery({
    queryKey: ['portal', 'subscriptions', id],
    queryFn: () => api.get<PortalSubscriptionDetailDto>(`/api/portal/subscriptions/${id}`),
    enabled: Boolean(id),
  });

  if (detail.isPending) return <Spinner label="Loading subscription…" />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const { subscription, resources, usage, limits, limitDescriptors, modules } = detail.data;

  return (
    <div>
      <Link
        to="/portal/subscriptions"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-600 dark:text-slate-400"
      >
        <ArrowLeft className="size-4" /> My Subscriptions
      </Link>
      <PageHeader
        title={subscription.name}
        description={`${subscription.planName} plan`}
        actions={
          <span className="flex gap-1.5">
            <Badge tone={statusTone(subscription.health)}>{subscription.health}</Badge>
            <Badge tone={statusTone(subscription.status)}>{subscription.status}</Badge>
            {subscription.overQuota && <Badge tone="red">over quota</Badge>}
          </span>
        }
      />

      <div className="space-y-6">
        <Card>
          <CardHeader
            title="Usage & limits"
            description="What your plan includes and how much of it is in use"
          />
          <div className="space-y-4 px-5 py-4">
            {limitDescriptors
              .filter((descriptor) => descriptor.hasUsage)
              .map((descriptor) => (
                <div key={descriptor.key}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span>{descriptor.label}</span>
                    <span className="tabular-nums text-slate-500 dark:text-slate-400">
                      {usage[descriptor.key] ?? 0} /{' '}
                      {formatLimitAmount(limits[descriptor.key], descriptor.kind)}
                    </span>
                  </div>
                  <MeterBar
                    value={usage[descriptor.key] ?? 0}
                    max={limits[descriptor.key] ?? null}
                  />
                </div>
              ))}
            <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 text-sm dark:border-slate-800 sm:grid-cols-4">
              {limitDescriptors
                .filter((descriptor) => !descriptor.hasUsage)
                .map((descriptor) => (
                  <div key={descriptor.key}>
                    <p className="text-slate-500 dark:text-slate-400">{descriptor.label}</p>
                    <p className="font-medium">
                      {formatLimitAmount(limits[descriptor.key], descriptor.kind)}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Resources"
            description="Applications, databases and services in this subscription"
          />
          {resources.length === 0 ? (
            <EmptyState icon={<Boxes className="size-10" />} title="No resources yet" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-3 font-medium">Name</th>
                    <th className="px-5 py-3 font-medium">Kind</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {resources.map((resource) => (
                    <tr
                      key={resource.id}
                      className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <td className="px-5 py-3">
                        <Link
                          to={`/portal/resources/${resource.id}`}
                          className="font-medium hover:text-brand-600"
                        >
                          {resource.name}
                        </Link>
                      </td>
                      <td className="px-5 py-3 text-slate-600 capitalize dark:text-slate-300">
                        {resource.kind}
                      </td>
                      <td className="px-5 py-3">
                        <Badge tone={statusTone(resource.status)}>{resource.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {modules.databases &&
          canManageCustomerService(me.data, 'databases') &&
          detail.data.features.databases_manage && (
            <CustomerDatabaseCreate
              subscriptionId={subscription.id}
              features={detail.data.features}
            />
          )}
        {modules.services &&
          canManageCustomerService(me.data, 'services') &&
          detail.data.features.services_manage &&
          detail.data.features.services_blueprint_install && (
            <CustomerServiceCreate
              subscriptionId={subscription.id}
              features={detail.data.features}
            />
          )}
        {modules.backups && <BackupsCard subscriptionId={subscription.id} />}
        {modules.backups && <SubscriptionProtectionPanel subscriptionId={subscription.id} portal />}
        {modules.mail && <CustomerMailPanel subscriptionId={subscription.id} />}
        {modules.dns && <DnsSummary subscriptionId={subscription.id} portal />}
        {modules.files &&
          (canManageCustomerService(me.data, 'files') &&
          detail.data.features[FILES_MANAGE_FEATURE] &&
          detail.data.features.files_file_access ? (
            <SubscriptionFilesPanel subscriptionId={subscription.id} customer />
          ) : (
            <FilesCard subscriptionId={subscription.id} />
          ))}
      </div>
    </div>
  );
}
